import { DEFAULT_NOTIFICATION_DURATION_MS } from 'constants/utils';
import { useState, useEffect } from 'react';

type NotificationType = 'error' | 'success' | null;

interface NotificationHook {
  notification: { text: string; type: NotificationType };
  showNotification: (
    newMessage: string,
    messageType: 'error' | 'success',
  ) => void;
}

/**
 * Custom hook for managing notifications.
 *
 * @param initialMessageParam - Initial notification message and type.
 * @param duration - Duration for which the notification is displayed (in milliseconds).
 * @returns Object with notification state and function to show new notifications.
 */
export const useNotification = (
  initialMessageParam: { text: string; type: NotificationType } = {
    text: '',
    type: null,
  },
  duration: number = DEFAULT_NOTIFICATION_DURATION_MS,
): NotificationHook => {
  const [notification, setNotification] = useState<{
    text: string;
    type: NotificationType;
  }>(initialMessageParam);

  useEffect(() => {
    if (notification.text) {
      const timer = setTimeout(() => {
        setNotification({ text: '', type: null });
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [notification.text, duration]);

  const showNotification = (
    newNotification: string,
    notificationType: 'error' | 'success',
  ): void => {
    setNotification({ text: newNotification, type: notificationType });
  };

  return {
    notification,
    showNotification,
  };
};
